/* =============================================================================
   HAKI native cart drawer — controller. Renders from window.HakiCart (the
   optimistic store) and wires the UI. See CART-SPEC.md.
   ============================================================================= */
(function () {
  'use strict';

  var root = document.querySelector('[data-haki-cart]');
  if (!root) return;
  // NB: do NOT bail if the store isn't ready yet — open/close are DOM-only and
  // must work immediately (the cart button can be clicked during page load).
  // The store-dependent wiring (render/subscribe) runs once HakiCart exists.

  // ---- config --------------------------------------------------------------
  var tiers = [];
  // variantId -> true once Shopify refused to add the gift (422: sold out). The
  // gift listings stop selling at 0 (PLM keeps the Mystery Hat (Gift) count equal
  // to the real hat pool), and without this reconcileGifts would retry the add on
  // every cart refresh forever while the tile showed a loading skeleton. Kept for
  // THIS page view only, so a restock is picked up on the next page.
  var soldOutGifts = {};
  function markSoldOut(vid) {
    return function (e) { if (e && e.status === 422) soldOutGifts[String(vid)] = true; };
  }
  try { tiers = JSON.parse(document.querySelector('[data-haki-cart-tiers]').textContent) || []; } catch (e) {}
  // A/B experiment on the tiers (assets/haki-experiments.js). A running test
  // with this visitor in a non-control arm hands back a full tiers array in
  // PLM's authoring shape (dollars, snake_case) — convert to the drawer's shape
  // (cents, camelCase) exactly as sections/haki-cart.liquid does for the live
  // metafield. The control arm returns null and the live tiers stand.
  var tiersOverride = null;
  try { tiersOverride = window.hakiExp && window.hakiExp.config('cart_tiers'); } catch (e) {}
  if (Array.isArray(tiersOverride) && tiersOverride.length) {
    tiers = tiersOverride.map(function (t) {
      return {
        type: t.type, threshold: Math.round(Number(t.threshold || 0) * 100), label: t.label || '',
        giftVariantId: t.gift_variant_id || '', remainingText: t.remaining_text || '', reachedText: t.reached_text || '',
        countries: Array.isArray(t.countries) ? t.countries : []
      };
    });
    tiers.__experiment = true;
  }
  tiers.sort(function (a, b) { return a.threshold - b.threshold; });

  // Where this bag is shipping to, server-rendered by haki-cart.liquid from
  // localization.country.iso_code. Switching country reloads the page (Shopify's
  // selector POSTs), so a server value can't go stale mid-session; Shopify's own
  // global is the fallback for any surface that renders the drawer without it.
  var shopperCountry = String(root.getAttribute('data-hk-country') ||
    (window.Shopify && window.Shopify.country) || '').toUpperCase();

  // A tier can be limited to specific countries, because the Shopify discount
  // BEHIND it can be. Ours is "Free Shipping over $100 (US only)"
  // (DiscountCountries: ["US"], includeRestOfWorld: false), but the drawer used to
  // tick "FREE SHIPPING" for everyone — so an international shopper was promised
  // free shipping and then charged for it at checkout. Tiers with no country list
  // (the gifts, whose 100%-off discount really is worldwide) stay available to all.
  function tierAvailable(t) {
    if (!t.countries || !t.countries.length) return true;
    // Country unknown (single-market store, or the attribute missing): show the
    // tier. Hiding a reward the shopper IS entitled to is the worse failure.
    if (!shopperCountry) return true;
    // Case-normalised on BOTH sides. The metafield is free text an operator types
    // in PLM, so "us" is as likely as "US" — and an unmatched code here would hide
    // free shipping from the ~99% of shoppers who are entitled to it, turning a fix
    // into a worse bug than the one it replaced.
    for (var i = 0; i < t.countries.length; i++) {
      if (String(t.countries[i]).trim().toUpperCase() === shopperCountry) return true;
    }
    return false;
  }
  // Gifts PLM has paused because they are about to run out (shop metafield
  // custom.paused_gifts, written by plm lib/website/gift-tier-guard.ts, printed as
  // ",id,id," by snippets/haki-paused-gifts.liquid). Only all-digit ids count, so a
  // missing, empty or malformed value pauses nothing and the bar is today's bar.
  function parsePausedGifts(raw) {
    var out = {};
    String(raw == null ? '' : raw).split(',').forEach(function (id) {
      id = id.trim();
      if (/^\d+$/.test(id)) out[id] = true;
    });
    return out;
  }
  var pausedGifts = parsePausedGifts(root.getAttribute('data-hk-paused-gifts'));
  function isPausedGift(t) {
    return !!(t && t.type === 'free_product' && t.giftVariantId && pausedGifts[String(t.giftVariantId)] === true);
  }
  // Country filter first, once, for every consumer below.
  var tiersWereOverridden = !!tiers.__experiment;
  tiers = tiers.filter(tierAvailable);
  // A pause switches off two things only: the PROMISE and the AUTO-ADD.
  //   tiers        = what we promise: the ladder, the meter's top, the "X more for"
  //                  prompt, the strip, the /cart band. Paused gifts are NOT in it.
  //   policedTiers = every gift we still recognise and police, paused or not: what
  //                  counts as a gift line, removal under the threshold, the 1-unit
  //                  cap, decline, and the tile with its X.
  // Filtering one list for both (PR #298) orphaned a paused gift already in the bag:
  // no tile, no X, no removal under the threshold, exactly while stock was short.
  var policedTiers = tiers;
  tiers = tiers.filter(function (t) { return !isPausedGift(t); });
  // The drawer's announcement strip is server-rendered from the LIVE tiers, so a
  // variant shopper would read "$100+" above a bar that says $85. Rebuild it from
  // the arm's tiers so the two never disagree (same wording as the Liquid).
  if (tiersWereOverridden) {
    try {
      var strip = root.querySelector('[data-hk-cart-annc]');
      if (strip && !strip.getAttribute('data-hk-annc-custom')) {
        var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
        var sfx = cur !== 'USD' ? ' USD' : '';
        strip.innerHTML = tiers.map(function (t, i) {
          var only = (t.countries && t.countries.length) ? ' (' + t.countries.join(', ').toUpperCase() + ' ONLY)' : '';
          var txt = String(t.label || '').toUpperCase() + ' ON ORDERS $' + Math.round(t.threshold / 100) + sfx + '+' + only;
          return '<span class="hk-cart__annc-msg' + (i === 0 ? ' is-on' : '') + '">' + esc(txt) + '</span>';
        }).join('');
      }
    } catch (e) {}
  }
  var recsCount = parseInt(root.getAttribute('data-recs-count'), 10);
  if (isNaN(recsCount)) recsCount = 6;
  // From the POLICED list: a paused gift in the bag is still a gift line (never
  // counted toward the paid subtotal, never drawn as a paid item).
  var giftIds = policedTiers.filter(function (t) { return t.type === 'free_product' && t.giftVariantId; })
                     .map(function (t) { return String(t.giftVariantId); });
  // While a cart write is in flight the steppers are disabled so the shopper
  // can't fire overlapping quantity changes (which raced the gift reconcile).
  // busyKey is the line whose stepper shows the spinner.
  var cartBusy = false;
  var busyKey = null;
  // After a line re-render (renderItems replaces innerHTML) the control the shopper
  // just used is destroyed, dropping focus to <body> — the complaint's ¶12 barrier
  // ("focus moved back to the first item… Plaintiff lost their place"). We remember
  // the intent here and re-land focus on the equivalent new control after each render.
  // It stays "sticky" across the cascade of re-renders one action can trigger (e.g. a
  // qty bump that crosses a tier and auto-adds a free gift = another write/re-render),
  // and is cleared only when the shopper moves focus themselves. See restorePendingFocus().
  var pendingFocus = null;
  // Same idea for the gift tiles: declining a gift removes the X that had focus.
  // { vid, index } of the declined X; see declineFocusTarget() / restoreGiftFocus().
  var pendingGiftFocus = null;
  var restoringFocus = false; // guards our own .focus() calls from clearing pendingFocus

  // ---- refs ----------------------------------------------------------------
  var $ = function (s) { return root.querySelector(s); };
  var overlay = $('[data-hk-cart-overlay]');
  var drawer = $('.hk-cart__drawer');
  var itemsEl = $('[data-hk-cart-items]');
  var emptyEl = $('[data-hk-cart-empty]');
  var footerEl = $('[data-hk-cart-footer]');
  var progressEl = $('[data-hk-cart-progress]');
  var subtotalAmt = $('[data-hk-cart-subtotal-amount]');
  var subtotalLbl = $('[data-hk-cart-subtotal-label]');
  var checkoutAmt = $('[data-hk-cart-checkout-amount]');
  var recsWrap = $('[data-hk-cart-recs]');
  var recsGrid = $('[data-hk-cart-recs-grid]');

  // ---- helpers -------------------------------------------------------------
  // Format in the shopper's ACTIVE presentment currency. Cart amounts (and, after
  // tierThreshold() below, the thresholds too) are in that currency's subunits, so
  // a hardcoded '$' printed a Cordoba bag as "$1302.00" — right number, wrong money.
  // Intl also gets the decimals right: JPY/KRW have none, and `/100 .toFixed(2)`
  // rendered them as "1,000.00" of a unit that has no cents.
  // currencyDisplay stays 'symbol' (not 'narrowSymbol') deliberately: 'symbol' keeps
  // USD as a bare "$" while disambiguating the other dollars ("CA$"), where
  // 'narrowSymbol' would render CAD as "$" too and mislead exactly the shopper this
  // is meant to inform.
  var moneyFmt = null;   // { code, fmt } — rebuilt only when the currency changes
  function activeCurrency() {
    var c = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    if (!c) {
      var st = window.HakiCart && window.HakiCart.getState && window.HakiCart.getState();
      c = st && st.currency;
    }
    return String(c || 'USD').toUpperCase();
  }
  function money(c) {
    var amt = Math.round(c) / 100;
    var code = activeCurrency();
    if (!moneyFmt || moneyFmt.code !== code) {
      moneyFmt = { code: code, fmt: null };
      var loc = (window.Shopify && window.Shopify.locale) || 'en';
      // Intl throws RangeError on an unknown locale/currency code rather than
      // falling back, so a bad value must not take the whole cart down with it.
      try { moneyFmt.fmt = new Intl.NumberFormat(loc, { style: 'currency', currency: code }); }
      catch (e) {
        try { moneyFmt.fmt = new Intl.NumberFormat('en', { style: 'currency', currency: code }); } catch (e2) {}
      }
    }
    return moneyFmt.fmt ? moneyFmt.fmt.format(amt) : '$' + amt.toFixed(2);
  }
  // /cart.js totals are in the shopper's ACTIVE presentment currency (Shopify
  // Markets currency conversion — e.g. NIO), but tier thresholds are always
  // authored in USD (see plm/lib/website/cart-tiers-core.ts). Without this, a
  // bag priced in a currency with a large USD rate (NIO is ~36:1) blows past
  // every tier on a single item, because the raw NUMBER (e.g. 1302) clears a
  // threshold meant to be read as dollars (130). Same fix as the legacy cart
  // page's free-shipping bar (assets/cart.js: `minSpend * Shopify.currency.rate`).
  function currencyRate() {
    var r = window.Shopify && window.Shopify.currency && Number(window.Shopify.currency.rate);
    return (r && isFinite(r) && r > 0) ? r : 1;
  }
  function tierThreshold(t) { return Math.round(t.threshold * currencyRate()); }
  // SR live region (sits outside the aria-hidden cart root, see haki-cart.liquid)
  var statusEl = document.querySelector('[data-hk-cart-status]');
  // Messages said within the same moment are read together, not the last one alone
  // (same as say() on the /cart page): a gift removal and the cart total can land in
  // one tick.
  var announceBuf = [], announceT = null;
  function announce(msg) {
    if (!statusEl || !msg) return;
    announceBuf.push(msg);
    statusEl.textContent = '';
    clearTimeout(announceT);
    announceT = setTimeout(function () { statusEl.textContent = announceBuf.join(' '); announceBuf = []; }, 50);
  }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (m) { return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[m]; }); }
  function giftProps(it) {
    var p = it && it.properties;
    if (!p || typeof p !== 'object') return false;
    return String(p._hk_gift || '') === '1';
  }
  function lineVariantId(it) { return String((it && (it.variant_id || it.id)) || ''); }
  // A gift is identified by its VARIANT, never by our own _hk_gift property.
  // Shopify keys cart lines on variant + properties, so a gift added by anyone
  // else — a shopper opening /products/gift-... directly, whose Add to Cart is a
  // plain form POST carrying no properties — lands as a SEPARATE line at qty 1,
  // not as our tagged line at qty 2. Keying on the property made every such line
  // invisible: the dedupe below could never fire, reconcileGifts saw "no gift"
  // and added a second one, and paidCents counted the freebie's own price toward
  // the next tier. Orders #HS118141 (2026-08-10) and #HS118791 (2026-08-29).
  function isGift(it) {
    return giftIds.indexOf(lineVariantId(it)) !== -1 || giftProps(it);
  }
  // EVERY line holding this gift variant, in cart order, however it was added.
  function findGiftLines(state, variantId) {
    return ((state && state.items) || []).filter(function (it) {
      return lineVariantId(it) === String(variantId);
    });
  }
  // The line we keep: prefer one we tagged (it carries _hk_tier, and preferring
  // it means the ordinary single-gift cart needs no write at all), else the first.
  function findGiftLine(state, variantId) {
    var lines = findGiftLines(state, variantId);
    for (var i = 0; i < lines.length; i++) if (giftProps(lines[i])) return lines[i];
    return lines[0] || null;
  }
  // Corso/GSP shipping protection (SKU "GSP-750") — a service line the app injects,
  // not a product the shopper chose, so it never counts toward "items".
  function isProtection(it) {
    var sku = String(it.sku || '').toUpperCase();
    var title = String(it.product_title || it.title || '').toLowerCase();
    return sku.indexOf('GSP') === 0 || title.indexOf('shipping protection') !== -1;
  }
  function normalizedProps(it) {
    var props = (it && it.properties && typeof it.properties === 'object') ? it.properties : {};
    var out = {};
    Object.keys(props).sort().forEach(function (key) {
      if (key === '_hk_gift' || key === '_hk_tier') return;
      var val = props[key];
      if (val == null || val === '') return;
      out[key] = val;
    });
    return JSON.stringify(out);
  }
  function mergeKey(it) {
    return [
      String(it.variant_id || it.id || ''),
      String(it.selling_plan_allocation && it.selling_plan_allocation.selling_plan && it.selling_plan_allocation.selling_plan.id || ''),
      normalizedProps(it)
    ].join('|');
  }
  function mergedItems(state) {
    var src = ((state && state.items) || []).filter(function (it) { return !isGift(it); });
    var groups = [];
    var byKey = {};
    for (var i = 0; i < src.length; i++) {
      var it = src[i];
      var key = mergeKey(it);
      var group = byKey[key];
      if (!group) {
        group = {
          key: it.key,
          mergeKeys: [it.key],
          quantity: it.quantity || 0,
          final_line_price: it.final_line_price != null ? it.final_line_price : (it.price || 0) * (it.quantity || 0),
          original_line_price: it.original_line_price != null ? it.original_line_price : (it.price || 0) * (it.quantity || 0),
          _merged: false,
          _sourceCount: 1
        };
        for (var prop in it) {
          if (Object.prototype.hasOwnProperty.call(it, prop) && group[prop] == null) group[prop] = it[prop];
        }
        byKey[key] = group;
        groups.push(group);
        continue;
      }
      group.mergeKeys.push(it.key);
      group.quantity += it.quantity || 0;
      group.final_line_price += it.final_line_price != null ? it.final_line_price : (it.price || 0) * (it.quantity || 0);
      group.original_line_price += it.original_line_price != null ? it.original_line_price : (it.price || 0) * (it.quantity || 0);
      group._merged = true;
      group._sourceCount += 1;
    }
    return groups;
  }
  // "Items" = only what the customer chose to buy: exclude the auto-added free gift
  // and the shipping-protection service. They still render as their own labeled
  // lines/box — they're just not tallied (matches purchase intent; see UX note).
  function paidItemCount(state) {
    return ((state && state.items) || []).reduce(function (n, it) {
      return (isGift(it) || isProtection(it)) ? n : n + (it.quantity || 0);
    }, 0);
  }

  // paid subtotal = everything except gift lines (for tier thresholds)
  function paidCents(state) {
    return (state.items || []).reduce(function (s, it) {
      if (isGift(it)) return s;
      return s + (it.original_line_price != null ? it.original_line_price : (it.price || 0) * (it.quantity || 0));
    }, 0);
  }

  // ---- open / close --------------------------------------------------------
  // The drawer is ALWAYS in the DOM (visibility, not display:none — see CSS) so
  // Corso can decorate the checkout button; open/close only toggle .is-open.
  var lastFocus = null;
  function open() {
    if (root.classList.contains('is-open')) return;
    lastFocus = document.activeElement;
    root.setAttribute('aria-hidden', 'false');
    if (window.hakiTag) window.hakiTag('cart_opened', 'yes');
    // The bar is only SEEN when the drawer opens — that is the exposure, not the
    // page load. Fires once per session; PLM dedupes per visitor for good.
    try { if (window.hakiExp && tiers.length) window.hakiExp.expose('cart_tiers'); } catch (e) {}
    // Lock the page: measure the scrollbar/gutter width and compensate with
    // padding so (a) the page doesn't shift ("zoom") when the scrollbar goes,
    // and (b) the fixed drawer can span the FULL viewport (no right-edge gutter).
    var comp = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--hk-scrollbar-comp', comp + 'px');
    document.documentElement.classList.add('hk-cart-open');
    requestAnimationFrame(function () {
      root.classList.add('is-open'); animateProgressIn(); nudgeCorso(); fitAccelerated();
      // Move focus into the dialog AFTER is-open makes it visible. .hk-cart is
      // visibility:hidden until then, and .focus() on a hidden element is a no-op,
      // so focusing here (not before the rAF) is what actually lands focus in the drawer.
      var close = $('[data-hk-cart-close]'); if (close) close.focus();
    });
    // Guard the ATC race: some add-to-cart flows re-enable and re-focus the PDP
    // "Add to cart" button AFTER we open, yanking focus back out of the dialog
    // (complaint: cart "not immediately given focus"). If focus left the drawer,
    // re-assert it once things settle.
    setTimeout(function () {
      if (root.classList.contains('is-open') && !drawer.contains(document.activeElement)) {
        var c = $('[data-hk-cart-close]'); if (c) c.focus();
      }
    }, 350);
    loadRecs();
  }
  function close() {
    pendingFocus = null; // drop any pending stepper-refocus intent
    pendingGiftFocus = null;
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('hk-cart-open');
    document.documentElement.style.removeProperty('--hk-scrollbar-comp');
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }
  function toggle() { root.classList.contains('is-open') ? close() : open(); }

  // ---- Corso nudge ---------------------------------------------------------
  // Corso skips buttons that are display:none and scans on cart change. Our
  // checkout button is inside the footer, which is display:none until the store
  // primes — so Corso's scan can miss it and (with a session cart) never re-run.
  // Re-inserting the button once it's visible fires Corso's element observer so
  // it decorates it. No-op once Corso has decorated (data-corso-checkout-plus).
  function nudgeCorso() {
    var btn = root.querySelector('[data-hk-cart-checkout-btn]');
    if (!btn || btn.dataset.corsoCheckoutPlus !== undefined) return;
    var p = btn.parentNode; if (!p) return;
    var n = btn.nextSibling;
    p.removeChild(btn);
    p.insertBefore(btn, n);
  }

  // ---- strip Rebuy's hidden Smart Cart -------------------------------------
  // Rebuy loads store-wide and leaves its (hidden) flyout in the DOM. Corso's
  // selector matches Rebuy's checkout button first and dumps the protection UI
  // into that invisible cart. Since the native cart REPLACES Rebuy, remove its
  // shell so Corso can only target OUR button, then nudge Corso to re-decorate.
  var stripScheduled = false;
  function stripRebuy() {
    var removed = false;
    document.querySelectorAll('.rebuy-cart__flyout, .rebuy-cart__overlay, .rebuy-cart-modal, [data-rebuy-cart]').forEach(function (el) {
      if (el.closest && el.closest('.hk-cart')) return; // never our own cart
      el.remove(); removed = true;
    });
    if (removed) nudgeCorso();
  }
  function scheduleStrip() {
    if (stripScheduled) return;
    stripScheduled = true;
    requestAnimationFrame(function () { stripScheduled = false; stripRebuy(); });
  }
  stripRebuy();
  new MutationObserver(scheduleStrip).observe(document.documentElement, { childList: true, subtree: true });

  // ---- collapse accelerated-checkout empty space ---------------------------
  // <shopify-accelerated-checkout-cart> reserves height for ALL wallets even
  // after we hide all but Shop Pay (CSS), leaving a gap. Measure the visible
  // Shop Pay button and clip the container to exactly its bottom. Retried for
  // the async hydration.
  function fitAccelerated() {
    var acc = root.querySelector('[data-hk-cart-accelerated]');
    if (!acc) return;
    var content = acc.querySelector('.dynamic-checkout__content') || acc;
    // Reset any prior clip BEFORE measuring, so we size against the fully-expanded
    // widget and — critically — never leave it clipped-to-hidden if this run can't
    // find the button (e.g. mid re-hydration). A visible gap beats a hidden button.
    content.style.maxHeight = '';
    content.style.overflow = '';
    var crect = content.getBoundingClientRect();
    // The wallet button row is button-height AND near full width (the vertical
    // layout stretches it), which distinguishes it from small text/icon nodes
    // that also fall in the height range during hydration.
    var nodes = content.querySelectorAll('*');
    var btn = null;
    for (var i = 0; i < nodes.length; i++) {
      if (getComputedStyle(nodes[i]).display === 'none') continue;
      var r = nodes[i].getBoundingClientRect();
      if (r.height >= 36 && r.height <= 80 && r.width >= crect.width * 0.6) { btn = nodes[i]; break; }
    }
    if (btn) {
      var b = btn.getBoundingClientRect();
      var clipH = Math.ceil((b.top - crect.top) + b.height + 4); // +4 so the button edge is never clipped
      // Only clip if the result is at least a real button tall — a tiny value
      // would mean we mis-measured, and clipping to it would hide the button.
      if (clipH >= 40) { content.style.maxHeight = clipH + 'px'; content.style.overflow = 'hidden'; }
    }
    // else: leave fully expanded (visible) — do NOT clip.
  }
  // Re-clip after a delay, retried across the widget's async (re)hydration.
  // Shopify re-renders <shopify-accelerated-checkout-cart> whenever the cart
  // changes (e.g. adding from recs), which resets our maxHeight clip and lets
  // the reserved multi-wallet gap balloon the footer upward — so we must re-fit
  // on every cart change, not just on open.
  var fitTimers = [];
  function scheduleFitAccelerated() {
    fitTimers.forEach(clearTimeout);
    // Delays give the widget time to (re)hydrate before we measure; the reset in
    // fitAccelerated keeps every attempt safe even if it fires mid-hydration.
    fitTimers = [300, 800, 1600].map(function (t) { return setTimeout(fitAccelerated, t); });
  }
  scheduleFitAccelerated();

  // ---- Shop Pay Installments -----------------------------------------------
  // The instalment figure is a PAYMENT CLAIM, so it is never allowed to describe
  // a bag other than the one on screen. This drawer updates its subtotal with
  // textContent and never re-renders the section, so the server-rendered terms
  // would otherwise freeze at the page-load total: add a second item and it
  // would still read "4 x $22.50" against a $170 bag.
  //
  // So: the markup is stamped with the total it was rendered for. The moment the
  // live total disagrees the line is HIDDEN, and a fresh copy is fetched through
  // the Section Rendering API so the numbers come from Shopify rather than from
  // arithmetic of ours. If that fetch fails, it stays hidden — showing nothing is
  // the only safe failure here.
  var termsEl = $('[data-hk-cart-payment-terms]');
  var termsTotal = null;   // the store's total the CURRENT markup describes
  var termsTimer = null;
  var termsSeq = 0;

  function syncPaymentTerms() {
    if (!termsEl) return;
    var state = window.HakiCart.getState();
    var live = state && typeof state.total_price === 'number' ? state.total_price : null;
    if (live === null) return;

    // Seed from the STORE on first run, never from Liquid's {{ cart.total_price }}.
    // They are not the same arithmetic — this store's total_price is
    // recomputed as subtotalCents(), which zeroes gift lines, so a bag with a
    // free gift would never match the Liquid figure. Comparing store-to-store
    // keeps it self-consistent; the server render is correct at page load, which
    // is the one moment we can take on trust.
    if (termsTotal === null) { termsTotal = live; termsEl.hidden = false; return; }
    if (live === termsTotal) { termsEl.hidden = false; return; }

    termsEl.hidden = true;
    clearTimeout(termsTimer);
    var seq = ++termsSeq;
    termsTimer = setTimeout(function () {
      fetch(window.Shopify.routes.root + '?sections=haki-cart', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : Promise.reject(new Error(r.status)); })
        .then(function (data) {
          // A newer cart change already superseded this request — its own fetch
          // owns the element now, so this stale response must not paint.
          if (seq !== termsSeq) return;
          var html = data['haki-cart'];
          if (!html) return;
          var fresh = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector('[data-hk-cart-payment-terms]');
          if (!fresh) return;
          termsEl.innerHTML = fresh.innerHTML;
          // Stamp the total this fetch was ISSUED for, keeping the comparison
          // store-to-store. If the cart moved again while it was in flight, the
          // seq guard above already stopped us painting, so this cannot record a
          // total the markup does not describe.
          termsTotal = live;
          // Shopify renders nothing when the bag falls outside the eligible
          // range, so an empty response means "no terms", not "still loading".
          termsEl.hidden = !fresh.innerHTML.trim();
        })
        .catch(function () { /* stays hidden — better than a wrong price */ });
    }, 400);
  }

  // ---- rotating announcement (fade) ----------------------------------------
  (function rotateAnnouncement() {
    var bar = root.querySelector('[data-hk-cart-annc]');
    if (!bar) return;
    var msgs = bar.querySelectorAll('.hk-cart__annc-msg');
    if (msgs.length < 2) return;
    var i = 0;
    // Pause rotation while the bar is hovered or contains focus (WCAG 2.2.2).
    var hovered = false, focused = false;
    bar.addEventListener('mouseenter', function () { hovered = true; });
    bar.addEventListener('mouseleave', function () { hovered = false; });
    bar.addEventListener('focusin', function () { focused = true; });
    bar.addEventListener('focusout', function (e) {
      if (!e.relatedTarget || !bar.contains(e.relatedTarget)) focused = false;
    });
    setInterval(function () {
      if (document.hidden || hovered || focused) return;
      msgs[i].classList.remove('is-on');
      i = (i + 1) % msgs.length;
      msgs[i].classList.add('is-on');
    }, 3500);
  })();

  // Make open/close available to the early (head) interceptor IMMEDIATELY, and
  // honour any cart click that happened before this controller ran.
  window.HakiCartUI = { open: open, close: close, toggle: toggle };
  window.__hakiOpenCart = open;
  if (window.__hakiCartPending) { window.__hakiCartPending = false; open(); }

  overlay.addEventListener('click', close);
  $('[data-hk-cart-close]').addEventListener('click', close);
  // When the shopper deliberately moves focus (Tab, click) to another control, stop
  // re-landing on the last-used stepper. Our own restore .focus() sets restoringFocus,
  // so it doesn't count as a shopper move.
  drawer.addEventListener('focusin', function () { if (!restoringFocus) { pendingFocus = null; pendingGiftFocus = null; } });
  document.addEventListener('keydown', function (e) {
    if (!root.classList.contains('is-open')) return;
    if (e.key === 'Escape') { close(); return; }
    if (e.key !== 'Tab') return;
    // Focus trap: keep Tab within the drawer.
    var f = drawer.querySelectorAll('a[href], button:not([disabled]), input, select, [tabindex]:not([tabindex="-1"])');
    f = Array.prototype.filter.call(f, function (el) { return el.offsetParent !== null; });
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  // (Cart-toggle clicks are intercepted by the early head script, which calls
  //  window.__hakiOpenCart — set to open() above.)

  // Add flows (PDP, CTL, cross-sell) request opening via this event.
  document.addEventListener('haki-cart:open', function () {
    if (window.HakiCart) window.HakiCart.refresh().then(open); else open();
  });

  // ---- render --------------------------------------------------------------
  // Title without the "[PREORDER] " prefix (window.hakiDisplayTitle, layout/theme.liquid).
  // The Shopify title keeps it for Google Shopping / Shop / Meta; the page already said
  // pre-order before the shopper added it. Inline fallback so the drawer never depends on
  // load order.
  function displayTitle(t) {
    return window.hakiDisplayTitle ? window.hakiDisplayTitle(t)
      : String(t == null ? '' : t).replace(/^\s*(?:\[\s*pre-?order\s*\]|pre-?order\s*[-–—])\s*/i, '');
  }
  function lineHTML(it) {
    var img = it.image ? it.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2') : '';
    var gift = isGift(it);
    var title = esc(displayTitle(it.product_title || it.title));
    var variant = (it.variant_title && it.variant_title !== 'Default Title')
      ? '<p class="hk-cart__item-variant">' + esc(it.variant_title) + '</p>' : '';
    // Middle column: title, variant, then either a Free tag or the qty controls.
    // While the cart is settling, disable the steppers; the row being changed
    // shows a spinner (busyKey), so a shopper can't trigger overlapping writes.
    var loadingRow = cartBusy && String(it.key) === String(busyKey);
    var qtyDis = cartBusy ? ' disabled' : '';
    var mid = gift
      ? '<p class="hk-cart__item-gift">Free gift</p>'
      : '<div class="hk-cart__qty' + (loadingRow ? ' hk-cart__qty--loading' : '') + '" data-key="' + esc(it.key) + '"' + (it.mergeKeys && it.mergeKeys.length > 1 ? ' data-merge-keys="' + esc(it.mergeKeys.join(',')) + '"' : '') + '>' +
          '<button type="button" class="hk-cart__qty-btn hk-cart__qty-btn--minus" data-act="dec"' + qtyDis + ' aria-label="Decrease quantity for ' + title + '"></button>' +
          '<span class="hk-cart__qty-val">' + it.quantity + '</span>' +
          '<button type="button" class="hk-cart__qty-btn hk-cart__qty-btn--plus" data-act="inc"' + qtyDis + ' aria-label="Increase quantity for ' + title + '"></button>' +
        '</div>';
    // Right column: price (+ remove link for non-gift lines).
    var price = gift
      ? '<span class="hk-cart__item-price hk-cart__item-price--free">' + money(0) + '</span>'
      : '<span class="hk-cart__item-price">' + money(it.final_line_price != null ? it.final_line_price : it.price * it.quantity) + '</span>';
    var remove = gift ? '' : '<button type="button" class="hk-cart__item-remove" data-act="remove" data-key="' + esc(it.key) + '"' + (it.mergeKeys && it.mergeKeys.length > 1 ? ' data-merge-keys="' + esc(it.mergeKeys.join(',')) + '"' : '') + qtyDis + ' aria-label="Remove ' + title + '">Remove</button>';
    return '<div class="hk-cart__item">' +
      (img ? '<img class="hk-cart__item-img" src="' + esc(img) + '" alt="' + title + '" loading="lazy">' : '<span class="hk-cart__item-img"></span>') +
      '<div class="hk-cart__item-mid">' +
        '<a class="hk-cart__item-title" href="' + esc(it.url || '#') + '">' + title + '</a>' +
        variant + mid +
      '</div>' +
      '<div class="hk-cart__item-right">' + price + remove + '</div>' +
    '</div>';
  }

  function renderItems(state) {
    cartBusy = !!state.busy;
    if (!cartBusy) busyKey = null; // settled — clear the spinner target
    var all = state.items || [];
    // Gifts are NOT line items — they render as a gift box in the progress area.
    // Shopify BXGY can split one paid variant into multiple rows; collapse those
    // identical paid rows in the drawer so the shopper sees one coherent item.
    var items = mergedItems(state);
    itemsEl.innerHTML = items.map(lineHTML).join('');
    var empty = all.length === 0;
    emptyEl.hidden = !empty;
    footerEl.hidden = empty;
    itemsEl.hidden = empty;
    if (!empty) requestAnimationFrame(nudgeCorso); // footer now visible → let Corso find the button
    restorePendingFocus(empty);
  }

  // Re-land keyboard focus after the innerHTML rebuild wipes out the control the
  // shopper just operated (¶12). Runs after EVERY line render. While the drawer is
  // open, focus must never sit on <body>: if the settled control exists we focus it,
  // otherwise we hold focus on the close button so the shopper stays in the dialog.
  // pendingFocus persists across the re-render cascade (a tier gift auto-add fires
  // more writes) and is cleared only when the shopper moves focus themselves.
  function restorePendingFocus(empty) {
    if (!pendingFocus || !root.classList.contains('is-open')) return;
    var ae = document.activeElement;
    var closeBtn = $('[data-hk-cart-close]');
    // Respect a REAL focus position (the shopper, or a stepper our restore already
    // landed). Focus sitting on <body> (rebuild dropped it) OR on our Close "holder"
    // both still need to be upgraded to the actual control, so they don't count here.
    // (The focusin handler clears pendingFocus when the shopper moves focus themselves.)
    if (ae && ae !== document.body && ae !== closeBtn && drawer.contains(ae)) return;
    // Prefer the exact control just used — only once the cart has SETTLED (re-enabled).
    var target = null;
    if (!cartBusy && !pendingFocus.removed && !empty && pendingFocus.key) {
      target = itemsEl.querySelector('.hk-cart__qty[data-key="' + pendingFocus.key + '"] [data-act="' + pendingFocus.act + '"]');
      // The line key can change after the optimistic→server round-trip; fall back to
      // the same action on the first line so we still land on a real stepper.
      if (!target || target.disabled) target = itemsEl.querySelector('[data-act="' + (pendingFocus.act || 'inc') + '"]:not([disabled])');
    } else if (!cartBusy && (pendingFocus.removed || empty)) {
      target = itemsEl.querySelector('.hk-cart__qty-btn--plus:not([disabled])')
        || itemsEl.querySelector('.hk-cart__item-remove:not([disabled])');
    }
    if (target && typeof target.focus === 'function') {
      restoringFocus = true;
      try { target.focus(); } catch (e) {}
      restoringFocus = false;
      // Do NOT clear pendingFocus here: one action can trigger a CASCADE of re-renders
      // (e.g. this qty bump crosses a tier and auto-adds a free gift = another write),
      // each wiping the control again. Staying sticky lets us re-land every time. The
      // focusin handler clears it the moment the shopper moves focus themselves.
      return;
    }
    // Not settled yet (buttons disabled) / target unavailable: never leave focus on
    // <body> while the drawer is open — hold it on Close and upgrade next render.
    if ((ae === document.body || !drawer.contains(ae)) && closeBtn) {
      restoringFocus = true;
      try { closeBtn.focus(); } catch (e) {}
      restoringFocus = false;
    }
  }

  function renderTotals(state) {
    var sub = window.HakiCart.subtotalCents();
    var count = paidItemCount(state || window.HakiCart.getState());
    subtotalAmt.textContent = money(sub);
    subtotalLbl.textContent = 'Subtotal (' + count + (count === 1 ? ' item' : ' items') + ')';
    if (checkoutAmt) checkoutAmt.textContent = money(sub);
    // header badges — same paid-item count (gift + protection excluded)
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      if (el.textContent !== String(count)) el.textContent = String(count);
    });
    // keep the header cart links' accessible name in sync with the count
    var cartLabel = 'Cart, ' + count + (count === 1 ? ' item' : ' items');
    document.querySelectorAll('a.haki-header__cart, a[data-haki-cart-toggle], a[data-rebuy-cart-toggle]').forEach(function (el) {
      el.setAttribute('aria-label', cartLabel);
    });
  }

  // ---- SR change announcements (silent on initial load) ---------------------
  var lastAnnounced = null;
  function announceChanges(state) {
    var count = paidItemCount(state);
    var sub = window.HakiCart ? window.HakiCart.subtotalCents() : 0;
    if (lastAnnounced === null) { lastAnnounced = { count: count, sub: sub }; return; }
    if (count === lastAnnounced.count && sub === lastAnnounced.sub) return;
    var msg = count > lastAnnounced.count ? 'Item added to cart. '
      : count < lastAnnounced.count ? 'Item removed from cart. '
      : 'Cart updated. ';
    announce(msg + count + (count === 1 ? ' item' : ' items') + ', subtotal ' + money(sub) + '.');
    lastAnnounced = { count: count, sub: sub };
  }

  var SHIP_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="M3 7h11v8H3z"/><path d="M14 10h4l3 3v2h-7z"/><circle cx="7" cy="17" r="1.6"/><circle cx="17.5" cy="17" r="1.6"/></svg>';
  var GIFT_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><rect x="4" y="9" width="16" height="11"/><path d="M2 9h20v3H2zM12 9v11M12 9s-3-5-5-3 5 3 5 3zM12 9s3-5 5-3-5 3-5 3z"/></svg>';
  var CHECK_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="5 12 10 17 19 7"/></svg>';

  var progressPct = 0;          // last target (so we can re-shoot on open)
  function progressTarget(state) {
    var paid = paidCents(state);
    if (!tiers.length) return 0;
    var top = tierThreshold(tiers[tiers.length - 1]);
    return Math.max(0, Math.min(100, top ? (paid / top) * 100 : 0));
  }
  function renderProgress(state) {
    var paid = paidCents(state);
    if (!tiers.length) {
      // Nothing left to promise (every tier paused or outside this country). A paused
      // gift the shopper already earned still needs its tile and its X, so the area
      // shows for that alone and hides otherwise.
      var only = progressEl.querySelector('.hk-cart__gift-area');
      if (!only) { progressEl.innerHTML = '<div class="hk-cart__gift-area"></div>'; only = progressEl.firstChild; }
      var onlyHTML = giftAreaHTML(state, paid);
      setGiftArea(only, onlyHTML);
      progressEl.hidden = !onlyHTML;
      return;
    }
    progressEl.hidden = false;
    progressPct = progressTarget(state);
    var next = tiers.find(function (t) { return paid < tierThreshold(t); });
    var prompt;
    if (next) {
      prompt = (next.remainingText || '{amount} more for {label}.')
        .replace('{amount}', '<b>' + money(tierThreshold(next) - paid) + '</b>').replace('{label}', '<b>' + esc(next.label) + '</b>');
    } else {
      var topTier = tiers[tiers.length - 1];
      // Never celebrate a gift Shopify just refused as sold out (the tile below
      // says "all gone for now"; the headline must not contradict it).
      prompt = (topTier.type === 'free_product' && soldOutGifts[String(topTier.giftVariantId)])
        ? '<b>' + esc(topTier.label) + '</b> is all gone for now'
        : (topTier.reachedText || '{label} unlocked.').replace('{label}', '<b>' + esc(topTier.label) + '</b>');
    }
    // Build the static structure once; afterwards update IN PLACE so the fill's
    // width transition animates (a rebuilt element starts at its width = no anim).
    if (!progressEl.querySelector('.hk-cart__meter-fill')) {
      progressEl.innerHTML =
        '<div class="hk-cart__tiers">' + tiers.map(function (t, idx) {
          return '<div class="hk-cart__tier" data-tier="' + idx + '">' +
            '<span class="hk-cart__tier-ico"></span>' +
            '<span class="hk-cart__tier-label">' + esc(t.label) + '</span></div>';
        }).join('') + '</div>' +
        '<div class="hk-cart__meter"><div class="hk-cart__meter-fill" style="width:0%"></div></div>' +
        '<p class="hk-cart__prompt"></p>' +
        '<div class="hk-cart__gift-area"></div>';
    }
    // Update tier completion IN PLACE — toggle the class + swap the icon only when the
    // state actually changes, so a re-render never rebuilds the row from an empty
    // template (which read as the checkmarks flashing filled -> empty -> filled).
    tiers.forEach(function (t, idx) {
      var el = progressEl.querySelector('.hk-cart__tier[data-tier="' + idx + '"]');
      if (!el) return;
      var done = paid >= tierThreshold(t);
      if (el.classList.contains('is-complete') === done && el.getAttribute('data-done') === (done ? '1' : '0')) return;
      el.classList.toggle('is-complete', done);
      el.setAttribute('data-done', done ? '1' : '0');
      el.querySelector('.hk-cart__tier-ico').innerHTML = done ? CHECK_SVG : (t.type === 'free_product' ? GIFT_SVG : SHIP_SVG);
    });
    progressEl.querySelector('.hk-cart__prompt').innerHTML = prompt;
    progressEl.querySelector('.hk-cart__meter-fill').style.width = progressPct + '%';
    setGiftArea(progressEl.querySelector('.hk-cart__gift-area'), giftAreaHTML(state, paid));
  }

  // The earned gift renders here (NOT as a line item): a box with the gift +
  // an X to decline. If declined, a RE-ADD button. Auto-add is in reconcileGifts.
  // variantId -> true. Persisted for the session so the decline survives cart edits,
  // re-renders, and an external re-add (e.g. a still-loaded Rebuy gift tier).
  var declined = (function () { try { return JSON.parse(sessionStorage.getItem('hkDeclinedGifts') || '{}') || {}; } catch (e) { return {}; } })();
  function saveDeclined() { try { sessionStorage.setItem('hkDeclinedGifts', JSON.stringify(declined)); } catch (e) {} }
  // The decline also goes on the CART (hidden underscore attribute, lands on the
  // order as a customAttribute), so an order at/above a tier with no gift line
  // says "declined" instead of looking identical to a lost gift. Value = sorted,
  // comma-separated gift variant ids declined right now; '' removes it (Shopify
  // drops an attribute set to empty). Goes through the store's write queue, and
  // only when it differs from what the cart already holds, so it cannot loop.
  // Call it AFTER queueing the related remove/add, so the line change is sent first.
  var DECLINED_ATTR = '_hk_gift_declined';
  function syncDeclinedAttr() {
    var C = window.HakiCart;
    if (!C || typeof C.updateAttributes !== 'function') return;
    var want = Object.keys(declined).filter(function (k) { return declined[k]; }).sort().join(',');
    var st = C.getState();
    var have = (st && st.attributes && st.attributes[DECLINED_ATTR]) || '';
    if (have === want) return;
    var patch = {}; patch[DECLINED_ATTR] = want;
    C.updateAttributes(patch).catch(function () {});
  }
  var XSVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><line x1="5" y1="5" x2="19" y2="19"/><line x1="19" y1="5" x2="5" y2="19"/></svg>';
  // vid -> true once its gift box has actually rendered, so the intro animation
  // plays only on the loading->loaded transition (not on every unrelated re-render).
  // Cleared when the tier locks again or the gift is declined, so a fresh unlock re-animates.
  var shownGifts = {};
  var GIFT_SKELETON =
    '<div class="hk-cart__gift hk-cart__gift--loading" aria-hidden="true">' +
      '<span class="hk-cart__gift-img hk-cart__skel"></span>' +
      '<div class="hk-cart__gift-info">' +
        '<span class="hk-cart__skel hk-cart__skel--line"></span>' +
        '<span class="hk-cart__skel hk-cart__skel--line hk-cart__skel--short"></span>' +
      '</div>' +
    '</div>';
  // The gift SKUs are named "... (Gift)" in Shopify so they stay apart from the
  // sellable product. The box already says "Free gift", so the suffix is noise —
  // and in a half-width cell it costs a whole line of the title.
  function giftTitle(line) {
    return displayTitle(line.product_title || line.title || '').replace(/\s*\(\s*(?:free\s+)?gift\s*\)\s*$/i, '');
  }
  // True when /cart.js reports a discount code that actually applies to this cart.
  function hasAppliedCode(state) {
    return ((state && state.discount_codes) || []).some(function (d) { return d && d.applicable !== false; });
  }
  function giftAreaHTML(state, paid) {
    // ONE grid holding every tile in tier order — a gift box, a loading skeleton
    // or a re-add button, whichever that tier currently needs. They used to be
    // two separate grids, which meant a bag with one gift kept and one declined
    // rendered a box row AND a button row: two rows for two tiles, in a drawer
    // where vertical space is the whole problem. Same grid = always one row.
    var tiles = '';
    // policedTiers, not tiers: a paused gift that is in the bag keeps its tile and X.
    policedTiers.forEach(function (t) {
      if (t.type !== 'free_product' || !t.giftVariantId) return;
      var vid = String(t.giftVariantId);
      if (paid < tierThreshold(t)) { delete shownGifts[vid]; return; } // locked: reset intro
      var line = findGiftLine(state, vid);
      // Paused and not in the bag: offer nothing (no skeleton, no "all gone", no re-add).
      if (!line && isPausedGift(t)) { delete shownGifts[vid]; return; }
      if (line) {
        var img = line.image ? line.image.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2') : '';
        // "Free gift" only when it's actually $0 (the Shopify discount is live).
        // If it still costs money, show the real price so it never reads "Free"
        // while checkout would charge. Shoppers see this, so no staff wording:
        // the usual cause is a discount code that can't combine with the
        // free-gift discount, and only then do we say why. Without a code
        // (a setup problem on our side) the price stands alone.
        var lp = (line.final_line_price != null) ? line.final_line_price : (line.price || 0) * (line.quantity || 1);
        var tag = (lp === 0)
          ? '<div class="hk-cart__gift-tag">Free gift</div>'
          : '<div class="hk-cart__gift-tag hk-cart__gift-tag--unset">' + money(lp) +
              (hasAppliedCode(state) ? '<span class="hk-cart__gift-why">Not free with your code</span>' : '') +
            '</div>';
        // Play the intro only the first render after loading (when we hadn't shown it yet).
        var intro = shownGifts[vid] ? '' : ' hk-cart__gift--intro';
        shownGifts[vid] = true;
        var title = giftTitle(line);
        tiles += '<div class="hk-cart__gift' + intro + '">' +
          (img ? '<img class="hk-cart__gift-img" src="' + esc(img) + '" alt="' + esc(title) + '">' : '<span class="hk-cart__gift-img"></span>') +
          '<div class="hk-cart__gift-info"><div class="hk-cart__gift-title">' + esc(title) + '</div>' + tag + '</div>' +
          '<button type="button" class="hk-cart__gift-x" data-decline-gift="' + esc(vid) + '" data-key="' + esc(line.key) + '" aria-label="Decline free gift: ' + esc(title) + '">' + XSVG + '</button>' +
        '</div>';
      } else if (soldOutGifts[vid]) {
        delete shownGifts[vid];
        tiles += '<div class="hk-cart__gift-out" role="status">' + esc(t.label) + ': all gone for now</div>';
      } else if (declined[vid]) {
        delete shownGifts[vid];
        tiles += '<button type="button" class="hk-cart__gift-readd" data-readd-gift="' + esc(vid) + '">Re-add ' + esc(t.label) + '</button>';
      } else {
        // Unlocked, not declined, but the gift line isn't in the cart yet — it's
        // mid-add (the Shopify AJAX round-trip takes ~1-2s). Show a skeleton in
        // sync with the tier icon lighting up so the gift doesn't pop in abruptly.
        tiles += GIFT_SKELETON;
      }
    });
    return tiles ? '<div class="hk-cart__gifts">' + tiles + '</div>' : '';
  }

  // Swap the gift-area content and smoothly animate its height (the bottom edge
  // slides down/up) so the box grows/shrinks instead of flashing on and off.
  // Skips when the markup is unchanged so unrelated re-renders don't re-animate.
  var giftAreaCache = null;
  var giftAreaAnim = null;
  function setGiftArea(el, html) {
    if (!el || html === giftAreaCache) return;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var h0 = el.offsetHeight;
    giftAreaCache = html;
    el.innerHTML = html;
    restoreGiftFocus(el);
    if (reduce || !el.animate) return;
    var h1 = el.offsetHeight;
    if (h0 === h1) return; // no height change (e.g. skeleton -> real gift) — the intro fade covers it
    if (giftAreaAnim) giftAreaAnim.cancel();
    el.style.overflow = 'hidden';
    giftAreaAnim = el.animate([{ height: h0 + 'px' }, { height: h1 + 'px' }],
      { duration: 280, easing: 'cubic-bezier(.2, .7, .2, 1)' });
    giftAreaAnim.onfinish = giftAreaAnim.oncancel = function () { el.style.overflow = ''; giftAreaAnim = null; };
  }

  // Where keyboard focus goes after a gift is declined (its X is destroyed with the
  // tile). Pure, for tests/gift-decline-focus.test.js. `c` describes the gift area
  // AFTER the redraw: xs = the gifts that still have an X, in order; readds = the gifts
  // with a Re-add button; index = where the declined X sat among the X buttons.
  // Order: the next gift's X, else the previous one's, else this gift's Re-add button
  // (not drawn while it is paused), else the drawer's Close. Never <body> (2.4.3).
  function declineFocusTarget(c) {
    var xs = (c && c.xs) || [], readds = (c && c.readds) || [];
    var i = Math.max(0, (c && c.index) || 0);
    if (xs.length) return { kind: 'x', vid: String(xs[Math.min(i, xs.length - 1)]) };
    if (readds.indexOf(String(c && c.vid)) !== -1) return { kind: 'readd', vid: String(c.vid) };
    return { kind: 'close' };
  }
  // Runs after every gift-area redraw. Sticky like pendingFocus: one decline can
  // redraw the area more than once (optimistic, then the server's answer), and it
  // is cleared only when the shopper moves focus themselves.
  function restoreGiftFocus(area) {
    if (!pendingGiftFocus || !root.classList.contains('is-open')) return;
    var ae = document.activeElement;
    if (ae && ae !== document.body && drawer.contains(ae)) return; // focus is somewhere real
    var attr = function (sel, name) {
      return Array.prototype.map.call(area.querySelectorAll(sel), function (b) { return String(b.getAttribute(name)); });
    };
    var t = declineFocusTarget({
      xs: attr('[data-decline-gift]', 'data-decline-gift'), readds: attr('[data-readd-gift]', 'data-readd-gift'),
      index: pendingGiftFocus.index, vid: pendingGiftFocus.vid
    });
    var target = t.kind === 'x' ? area.querySelector('[data-decline-gift="' + t.vid + '"]')
      : t.kind === 'readd' ? area.querySelector('[data-readd-gift="' + t.vid + '"]') : null;
    if (!target) target = $('[data-hk-cart-close]');
    if (!target || typeof target.focus !== 'function') return;
    restoringFocus = true;
    try { target.focus(); } catch (e) {}
    restoringFocus = false;
  }
  // "<gift> removed." — the /cart page's words for the same act (haki-cart-page.js).
  // A gift changes neither the paid count nor the subtotal, so announceChanges() says
  // nothing about it (4.1.3).
  function announceGiftRemoved(line) {
    var name = line ? giftTitle(line) : '';
    announce((name || 'Gift') + ' removed.');
  }

  // Decline (X) / re-add clicks in the progress/gift area.
  progressEl.addEventListener('click', function (e) {
    var x = e.target.closest('[data-decline-gift]');
    if (x) {
      var dvid = x.getAttribute('data-decline-gift');
      var all = Array.prototype.slice.call(progressEl.querySelectorAll('[data-decline-gift]'));
      pendingGiftFocus = { vid: String(dvid), index: Math.max(0, all.indexOf(x)) };
      var gone = findGiftLine(window.HakiCart.getState(), dvid);
      declined[dvid] = true; saveDeclined();
      window.HakiCart.remove(x.getAttribute('data-key'));
      syncDeclinedAttr();
      announceGiftRemoved(gone);
      // Nothing redrew (the line was already gone): still never leave focus on <body>.
      restoreGiftFocus(progressEl);
      return;
    }
    var r = e.target.closest('[data-readd-gift]');
    if (r) {
      var vid = r.getAttribute('data-readd-gift');
      var tier = tiers.find(function (t) { return String(t.giftVariantId) === String(vid); });
      if (!tier) return; // paused since this button was drawn: never add it back
      delete declined[vid];
      saveDeclined();
      window.HakiCart.add(Number(vid) || vid, 1, {
        _hk_gift: '1',
        _hk_tier: String((tier && tier.threshold) || '')
      }).catch(markSoldOut(vid));
      syncDeclinedAttr();
    }
  });
  // Re-shoot the bar from 0 → target whenever the cart opens.
  function animateProgressIn() {
    var fill = progressEl.querySelector('.hk-cart__meter-fill');
    if (!fill) return;
    fill.style.transition = 'none';
    fill.style.width = '0%';
    // force reflow, then restore the transition and set the target
    void fill.offsetWidth;
    fill.style.transition = '';
    requestAnimationFrame(function () { fill.style.width = progressPct + '%'; });
  }

  // ---- free gift (theme-owned, driven by tier config) ----------------------
  var giftBusy = false;
  // Zero out every supplied line. Several changeLine calls in one tick is the
  // store's supported path: it coalesces per line key and drains them all before
  // it re-renders, so the drawer never paints a half-cleared cart. Returns true
  // if it had anything to do.
  function clearGiftLines(lines, done) {
    if (!lines.length) return false;
    giftBusy = true;
    lines.forEach(function (l) { window.HakiCart.changeLine(l.key, 0); });
    setTimeout(done, 300);
    return true;
  }
  function reconcileGifts(state) {
    if (giftBusy) return;
    var paid = paidCents(state);
    // policedTiers, not tiers: every rule below except the auto-add also applies to a
    // paused gift (decline, removal under the threshold, the 1-unit cap).
    for (var i = 0; i < policedTiers.length; i++) {
      var t = policedTiers[i];
      if (t.type !== 'free_product' || !t.giftVariantId) continue;
      var vid = String(t.giftVariantId);
      var lines = findGiftLines(state, vid);
      var has = findGiftLine(state, vid);
      var qualifies = paid >= tierThreshold(t);
      // Declined this session: never auto-add, and actively remove it if it came
      // back behind our back (the shopper re-adding it from the gift product page).
      if (declined[vid]) {
        if (clearGiftLines(lines, done)) return;
        // Dropping below the threshold clears the decline (a fresh unlock re-offers).
        if (!qualifies) { delete declined[vid]; saveDeclined(); syncDeclinedAttr(); }
        continue;
      }
      // The AUTO-ADD is the one thing a pause switches off.
      if (qualifies && !has && !soldOutGifts[vid] && !isPausedGift(t)) {
        giftBusy = true;
        var onFail = markSoldOut(vid);
        window.HakiCart.add(Number(t.giftVariantId) || t.giftVariantId, 1, {
          _hk_gift: '1',
          _hk_tier: String(t.threshold || '')
        }).then(done, function (e) { onFail(e); done(); });
        return;
      }
      // Not entitled to it — drop it, however it got here. This is what catches a
      // shopper who added the gift straight from /products/gift-... on a cart that
      // never reached the threshold.
      if (!qualifies) {
        // A paused gift leaves with nothing on screen to explain it (its tier is off
        // the ladder, so no "X away from" prompt follows): say which gift went.
        if (lines.length && isPausedGift(t)) announceGiftRemoved(has);
        if (clearGiftLines(lines, done)) return;
        continue;
      }
      // Over the threshold with no line: sold out, or paused (not added). Nothing to cap.
      if (!has) continue;
      // Over the threshold WITH a line: kept, paused or not. The shopper earned it (or
      // added it from the gift's own page while entitled); a pause never takes it back.
      // HARD CAP at 1 UNIT TOTAL across every line of this variant. The gift product
      // page is publicly reachable and its Add to Cart is a plain form POST with no
      // line properties, so a shopper-added copy is a SEPARATE line, not extra
      // quantity on ours — the old check (`has.quantity > 1`) could never see it.
      // Keep `has`, zero the rest, clamp what remains to 1. Self-healing: a cart
      // that already holds a double corrects itself on the next update.
      var extras = lines.filter(function (l) { return l.key !== has.key; });
      var overQty = (has.quantity || 1) > 1;
      if (extras.length || overQty) {
        giftBusy = true;
        extras.forEach(function (l) { window.HakiCart.changeLine(l.key, 0); });
        if (overQty) window.HakiCart.changeLine(has.key, 1);
        setTimeout(done, 300);
        return;
      }
    }
    function done() { giftBusy = false; }
  }

  // ---- recommendations (pluggable provider) --------------------------------
  var recsLoaded = false;
  function loadRecs() {
    if (recsLoaded || recsCount <= 0 || !window.HakiCart) return;
    recsLoaded = true;
    var state = window.HakiCart.getState();
    var pid = (state.items && state.items[0] && state.items[0].product_id) || null;
    window.HakiCart.recommendations.get({ productId: pid, limit: recsCount }).then(function (products) {
      products = (products || []).filter(function (p) { return p && p.available !== false; }).slice(0, recsCount);
      if (!products.length) { recsWrap.hidden = true; return; }
      recsGrid.innerHTML = products.map(recHTML).join('');
      recsWrap.hidden = false;
    });
  }
  function recHTML(p) {
    var v = (p.variants && p.variants[0]) || {};
    var img = (p.featured_image || (p.images && p.images[0]) || '');
    var hasOpts = p.variants && p.variants.length > 1;
    var sel = hasOpts ? '<select class="hk-cart__rec-select" data-rec-select aria-label="Options for ' + esc(displayTitle(p.title)) + '">' +
        p.variants.map(function (vv) { return '<option value="' + vv.id + '"' + (vv.available === false ? ' disabled' : '') + '>' + esc(vv.title) + '</option>'; }).join('') + '</select>'
      // No variant selector (single-variant products like hats): reserve the same
      // vertical space the selector would take so these cards match the spacing +
      // height of cards that have one, and the Add buttons line up across the grid.
      : '<span class="hk-cart__rec-spacer" aria-hidden="true"></span>';
    return '<div class="hk-cart__rec" data-variant="' + (v.id || '') + '">' +
      (img ? '<img class="hk-cart__rec-img" src="' + esc(img) + '" alt="' + esc(displayTitle(p.title)) + '" loading="lazy">' : '<span class="hk-cart__rec-img"></span>') +
      '<div class="hk-cart__rec-body">' +
        '<a class="hk-cart__rec-title" href="' + esc(p.url || ('/products/' + p.handle)) + '">' + esc(displayTitle(p.title)) + '</a>' +
        '<span class="hk-cart__rec-price">' + money(v.price != null ? v.price : (p.price || 0)) + '</span>' +
        sel +
        '<button type="button" class="hk-cart__btn hk-cart__btn--green hk-cart__rec-add" data-rec-add>Add</button>' +
      '</div></div>';
  }

  recsGrid.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-rec-add]'); if (!btn) return;
    var card = btn.closest('.hk-cart__rec');
    var sel = card.querySelector('[data-rec-select]');
    var id = sel ? sel.value : card.getAttribute('data-variant');
    if (!id) return;
    btn.disabled = true; var lbl = btn.textContent; btn.textContent = 'Adding…';
    window.HakiCart.add(Number(id) || id, 1).then(function () { btn.disabled = false; btn.textContent = lbl; });
  });

  // ---- line-item interactions (optimistic via store) -----------------------
  itemsEl.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-act]'); if (!btn) return;
    if (cartBusy) return; // a write is settling — ignore (buttons are disabled too)
    var act = btn.getAttribute('data-act');
    if (act === 'remove') {
      var rmKeys = String(btn.getAttribute('data-merge-keys') || btn.getAttribute('data-key') || '').split(',').filter(Boolean);
      busyKey = rmKeys[0] || null;
      pendingFocus = { removed: true }; // row disappears → re-land focus in the dialog
      rmKeys.forEach(function (key) { window.HakiCart.remove(key); });
      return;
    }
    var widget = btn.closest('.hk-cart__qty'); if (!widget) return;
    var key = widget.getAttribute('data-key');
    var item = (window.HakiCart.getState().items || []).find(function (i) { return i.key === key; });
    if (!item) return;
    var q = item.quantity + (act === 'inc' ? 1 : -1);
    busyKey = key; // this row shows the spinner until the cart settles
    pendingFocus = { key: key, act: act }; // re-land focus on this stepper after re-render (¶12)
    window.HakiCart.changeLine(key, q);
  });

  // ---- checkout ------------------------------------------------------------
  var coBtn = $('[data-hk-cart-checkout-btn]');
  if (coBtn) coBtn.addEventListener('click', function () {
    // Checkout takes a moment to load: the mini loader shows meanwhile (snippets/haki-loader).
    coBtn.setAttribute('aria-busy', 'true');
    window.location.href = '/checkout';
  });

  // ---- shared tier engine (read by the /cart page) -------------------------
  // The /cart page (assets/haki-cart-page.js) must show EXACTLY the drawer's
  // ladder: the same A/B arm, the same country filter, the same USD->presentment
  // conversion and the same idea of what a gift is. So it does not build its own:
  // it reads this one. `tiers` is the final, filtered, sorted list above.
  window.HakiCartTiers = {
    tiers: tiers,
    // Every gift variant we recognise, PAUSED ONES INCLUDED (tiers above leaves them
    // out). For "is this a gift product" checks such as the /cart picks.
    giftVariantIds: giftIds.slice(),
    isPausedGift: function (vid) { return pausedGifts[String(vid)] === true; },
    tierThreshold: tierThreshold,
    currencyRate: currencyRate,
    money: money,
    isGift: isGift,
    paidCents: paidCents,
    paidItemCount: paidItemCount,
    displayTitle: displayTitle,
    isSoldOut: function (vid) { return !!soldOutGifts[String(vid)]; },
    isDeclined: function (vid) { return !!declined[String(vid)]; },
    // Remove a gift line the way the drawer's X does, so reconcileGifts does not
    // put it straight back.
    declineGift: function (vid, key) {
      declined[String(vid)] = true; saveDeclined();
      if (key) window.HakiCart.remove(key);
      syncDeclinedAttr();
    },
    readdGift: function (vid) {
      vid = String(vid);
      var tier = tiers.find(function (t) { return String(t.giftVariantId) === vid; });
      if (!tier) return Promise.resolve(); // paused (or not a tier): never add it back
      delete declined[vid]; saveDeclined();
      var p = window.HakiCart.add(Number(vid) || vid, 1, {
        _hk_gift: '1', _hk_tier: String((tier && tier.threshold) || '')
      }).catch(markSoldOut(vid));
      syncDeclinedAttr();
      return p;
    }
  };
  try { document.dispatchEvent(new CustomEvent('haki-cart:tiers')); } catch (e) {}

  // ---- wire to the store (waits for it if not ready yet) -------------------
  function wireStore() {
    window.HakiCart.subscribe(function (state) {
      // Stamp the experiment assignment onto the cart so it lands on the order
      // (customAttributes _hk_vid / _hk_exp). No-op when nothing is running.
      try { if (window.hakiExp) window.hakiExp.syncCart(state); } catch (e) {}
      renderItems(state);
      renderTotals(state);
      renderProgress(state);
      // Only reconcile gifts once the cart has SETTLED (no write in flight).
      // Intermediate/optimistic states are skipped, so a burst of quantity edits
      // resolves to one gift add/remove against the final subtotal instead of
      // thrashing. Any gift op it fires flips busy back on, keeping the steppers
      // disabled seamlessly until that settles too.
      if (!state.busy) reconcileGifts(state);
      // The accelerated-checkout widget re-hydrates on cart changes and loses our
      // height clip; re-fit (only once settled) so the footer doesn't balloon.
      if (!state.busy) scheduleFitAccelerated();
      // Settled-only, like the two above: a burst of quantity edits resolves to
      // one section fetch against the final total instead of one per keystroke.
      if (!state.busy) syncPaymentTerms();
      announceChanges(state);
    });
    if (root.classList.contains('is-open')) loadRecs();
  }
  if (window.HakiCart) {
    wireStore();
  } else {
    var waitT = setInterval(function () {
      if (window.HakiCart) { clearInterval(waitT); wireStore(); }
    }, 30);
    setTimeout(function () { clearInterval(waitT); }, 8000);
  }
})();
